<template>
  <div class="programme-profile-view">
    <div v-if="programme" class="programme-header mb-base">
      <router-link :to="`/programme/${programmeId}`" class="text-aba-blue hover:underline">
        ← Back to {{ programme.title }}
      </router-link>
    </div>
    <content-with-sidebar class="profile">
      <template v-slot:main>
        <div v-if="loading" class="text-center p-base">
          Loading profile...
        </div>
        <div v-else-if="profile">
          <div class="text-block relative">
            <h1 class="text-5xl mt-sm">{{ displayName }}</h1>
            <h2 v-if="positionStr" class="mt-sm">{{ positionStr }}</h2>
            <div v-if="partners.length" class="partners my-base">
              <credits-string prefix="Supported by" :persons="partners" no-link/>
            </div>
            <div v-if="programmeParticipation.length" class="programme-participation my-base">
              <h3 class="text-xl mb-sm">Participation in {{ programme.title }}</h3>
              <div v-for="event in programmeParticipation" :key="event.postId" class="event-item mb-sm">
                <router-link
                  :to="`/programme/${programmeId}/${event.postId}`"
                  class="text-aba-blue hover:underline">
                  {{ event.title || `Event #${event.countNumber || ''}` }}
                </router-link>
                <span v-if="event.date" class="text-gray-600 text-sm ml-sm">
                  {{ formatDate(event.date) }}
                </span>
              </div>
            </div>
          </div>
          <attachments-view :attachments="attachments" />
          <div class="text-block mt-base" v-html="text" />
        </div>
        <div v-else class="text-center p-base text-red-600">
          Profile not found
        </div>
      </template>
      <template v-slot:sidebar>
        <profile-sidebar v-if="profile" :profile="profile"/>
        <div v-if="programme" class="programme-info mt-base p-base bg-gray-100 rounded">
          <h3 class="text-lg mb-sm">{{ programme.title }}</h3>
          <div v-if="programme.text" class="text-sm" v-html="programmeDescription" />
        </div>
      </template>
    </content-with-sidebar>
  </div>
</template>

<script>
import { db } from '../lib/firebase'
import { formatPeriod } from '../lib/date'
import DOMPurify from 'dompurify'
import ContentWithSidebar from './components/UI/layouts/ContentWithSidebar'
import ProfileSidebar from './ProfileSidebar'
import AttachmentsView from './components/AttachmentsView'
import CreditsString from './components/CreditsString'

export default {
  name: 'ProgrammeProfileView',
  components: { CreditsString, AttachmentsView, ProfileSidebar, ContentWithSidebar },
  props: {},

  data: () => ({
    unsubscribe: {},
    profile: null,
    programme: null,
    loading: true
  }),

  computed: {
    programmeId () { return this.$route.params.programmeId },
    profileId () { return this.$route.params.profileId },
    displayName () { return (this.profile || {}).displayName },
    positionStr () {
      const profile = (this.profile || {})
      const position = profile.abaPosition || ''
      switch (position) {
        case 'resident':
          return 'Residency: ' + formatPeriod(profile.residencyStart, profile.residencyEnd, 'en')
        case 'guest':
          return ''
        default:
          return position
      }
    },
    text () {
      return DOMPurify.sanitize((this.profile || {}).text || (this.profile || {}).description || '')
    },
    attachments () {
      return (this.profile || {}).attachments || {}
    },
    partners () {
      return [...(this.profile || {}).supportedBy || []].map(p => ({ id: p.id, displayName: p.title || p.name }))
    },
    programmeDescription () {
      if ((this.programme || {}).text) {
        return DOMPurify.sanitize(this.programme.text)
      }
      return ''
    },
    programmeParticipation () {
      if (!this.profile) return []
      // Return events loaded from loadProgrammeEvents
      return (this.profile.programmeEvents || [])
    }
  },

  created () {
    this.subscribeProgramme()
    this.subscribeProfile()
  },

  watch: {
    programmeId () {
      this.subscribeProgramme()
    },
    profileId () {
      this.subscribeProfile()
    }
  },

  beforeDestroy () {
    Object.values(this.unsubscribe).forEach(unsub => {
      if (typeof unsub === 'function') unsub()
    })
  },

  methods: {
    subscribeProgramme () {
      if (!this.programmeId) return
      this.unsubscribe.programme = db.collection('programmes')
        .doc(this.programmeId)
        .onSnapshot(
          snapshot => {
            if (snapshot.exists) {
              this.programme = { id: this.programmeId, ...snapshot.data() }
            }
          },
          err => {
            console.error('SUBSCRIBE PROGRAMME ERROR:', err)
          })
    },
    subscribeProfile () {
      if (!this.profileId) {
        this.loading = false
        return
      }
      this.loading = true
      this.unsubscribe.profile = db.collection('profiles')
        .doc(this.profileId)
        .onSnapshot(
          snapshot => {
            this.loading = false
            if (snapshot.exists) {
              this.profile = { uid: this.profileId, ...snapshot.data() }
              // Load programme participation events
              this.loadProgrammeEvents()
            } else {
              this.profile = null
            }
          },
          err => {
            console.error('SUBSCRIBE PROFILE ERROR:', err)
            this.loading = false
          })
    },
    loadProgrammeEvents () {
      if (!this.profileId || !this.programmeId) return
      // Get all events in this programme where this user is a participant
      db.collection('posts')
        .where('partOfProgramme.programmeId', '==', this.programmeId)
        .where('status', '==', 'public')
        .where('participants', 'array-contains', { id: this.profileId })
        .orderBy('date', 'desc')
        .get()
        .then(snapshot => {
          const events = []
          snapshot.forEach(doc => {
            const eventData = { ...doc.data(), postId: doc.id }
            events.push({
              postId: doc.id,
              title: eventData.title,
              date: eventData.date,
              countNumber: eventData.countNumber,
              excerpt: eventData.excerpt
            })
          })
          // Store in profile for display
          if (this.profile) {
            this.$set(this.profile, 'programmeEvents', events)
          }
        })
        .catch(err => {
          console.error('Error loading programme events:', err)
        })
    },
    formatDate (timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    }
  }
}
</script>

<style lang="scss">
  .programme-profile-view {
    .programme-header {
      padding: 1rem;
    }
    .programme-participation {
      .event-item {
        padding: 0.5rem;
        border-left: 2px solid #ccc;
        padding-left: 1rem;
      }
    }
    .programme-info {
      max-width: 100%;
    }
  }
</style>
